import type { StaffRole } from "@lafayette/auth";
import { useMemo } from "react";

import { type CapabilityRules, capabilityFlags } from "../access/capabilities";
import { useStaffRoles } from "../access/use-staff-roles";
import { useStaffSelf } from "./queries";

// What a staff member may do in the Requests UI. Presentation only: the API decides every request again (a concierge also needs the
// owner or customer relationship the rule asks for, and the API answers 403 without telling which). The roles come from the
// domain's `capabilityRoles`.

export type RequestCapabilities = Readonly<{
  /** `request.work`: the queue and every command that moves the work. */
  work: boolean;
  /** `request.reassign_owner`: a supervisor assigns or reassigns any open request. */
  reassignOwner: boolean;
  /** `request.internal_notes`: add an internal note. */
  internalNotes: boolean;
  /** `request.read_internal`: the detail with the history, the notes and the staff questions. */
  readInternal: boolean;
}>;

const requestCapabilityRules = {
  work: "request.work",
  reassignOwner: "request.reassign_owner",
  internalNotes: "request.internal_notes",
  readInternal: "request.read_internal",
} as const satisfies CapabilityRules;

export function requestCapabilitiesForRoles(roles: readonly StaffRole[]): RequestCapabilities {
  return capabilityFlags(requestCapabilityRules, roles);
}

export function useRequestCapabilities(): Readonly<{
  capabilities: RequestCapabilities;
  isLoading: boolean;
}> {
  const { roles, isLoading } = useStaffRoles();
  const capabilities = useMemo(() => requestCapabilitiesForRoles(roles), [roles]);
  return { capabilities, isLoading };
}

/**
 * The signed-in staff member as the server knows them (`GET /staff/me`), for the queue and the detail page. The id marks "yours" and
 * lets the detail page judge ownership. The capabilities from the server can only NARROW what the roles of the session allow, so a role
 * that Postgres no longer grants hides the action at once. While the answer loads, or when it fails, `staffUserId` is `null` and the
 * roles alone decide what is shown: the server still judges every command. `isLoading` stays true until the answer or its failure
 * arrives.
 */
export function useRequestViewer(): Readonly<{
  staffUserId: string | null;
  displayName: string | null;
  capabilities: RequestCapabilities;
  isLoading: boolean;
}> {
  const { capabilities: fromRoles, isLoading: rolesLoading } = useRequestCapabilities();
  const selfEnabled = !rolesLoading && fromRoles.work;
  const self = useStaffSelf(selfEnabled);
  const data = self.data;
  const capabilities = useMemo(() => {
    if (data === undefined) return fromRoles;
    const has = (code: (typeof data.capabilities)[number]) => data.capabilities.includes(code);
    return {
      work: fromRoles.work && has("request.work"),
      reassignOwner: fromRoles.reassignOwner && has("request.reassign_owner"),
      internalNotes: fromRoles.internalNotes && has("request.internal_notes"),
      readInternal: fromRoles.readInternal && has("request.read_internal"),
    };
  }, [data, fromRoles]);
  return {
    staffUserId: data?.staffUserId ?? null,
    displayName: data?.displayName ?? null,
    capabilities,
    // The pages wait for the answer (or its failure) before they draw an action, so a button never shows and then turns off.
    isLoading: rolesLoading || (selfEnabled && self.isPending),
  };
}
